import { useEffect, useState } from "react";
import { Banknote, CalendarCheck, CalendarClock, Ellipsis, FilePlus2, Inbox, Mail, MessageSquare, Pencil, Phone, Plus, Settings } from "lucide-react";
import { SCHEMES, THEMES, setScheme as setColorScheme, setTheme, type SchemeName, type ThemeName } from "@web/theme/theme";
import { ActionBar, Avatar, BottomSheet, Bubble, Button, Card, ChipStrip, EmptyState, FormField, Inline, MoneyField, MoneyText, MonthGrid, NavBar, NavBarAction, Page, Picker, Row, RowGroup, SearchBar, SegmentedControl, SelectChip, Skeleton, Stack, Stat, StatRow, StatusChip, SystemRow, TabBar, Text, TextArea, TextField, ThemeFrame, TimelineItem, ToastPreview, Toggle } from "@web/ui";
import { TABS } from "./tabs";
import { AppFrame, ContactSummary, LeadCard, ResponsiveFilters, TourRow, EventRow, Workspace, Region, FormSection, FormGrid, SaveBar } from "@web/ui";
import { leadCardLines } from "@shared/leadCard";
import { FileRow, ReviewChecklist, SignaturePad } from "@web/ui";

const WIDTHS = [390, 1280, 1440] as const;

/** Every Phase 0 component, rendered once per (theme × width). Staging-only link; no session needed. */
export function DevUi() {
  const params = new URLSearchParams(window.location.search);
  const preview = params.has("preview");
  const previewTheme = params.get("theme") === "minimal" ? "minimal" : "default";
  const previewScheme: SchemeName = params.get("scheme") === "dark" ? "dark" : params.get("scheme") === "light" ? "light" : "system";
  useEffect(() => { if (!preview) return; setTheme(previewTheme); setColorScheme(previewScheme); return () => { setTheme("default"); setColorScheme("system"); }; }, [preview, previewTheme, previewScheme]);
  const [scheme, setScheme] = useState<SchemeName>("system");
  if (preview) return <Gallery theme={previewTheme} />;
  return (
    <Page wide>
      <Stack gap={6}>
        <Stack gap={2}>
          <Text variant="large-title">Component gallery</Text>
          <Text variant="callout" tone="secondary">
            Real browser viewports at phone and laptop sizes. Each preview uses the same components with its own theme and color scheme.
          </Text>
          <Inline gap={2} wrap>
            {SCHEMES.map((s) => (
              <Button key={s} variant={s === scheme ? "primary" : "secondary"} fullWidth={false} onClick={() => setScheme(s)}>
                {s[0].toUpperCase() + s.slice(1)}
              </Button>
            ))}
          </Inline>
        </Stack>
        {THEMES.map((theme) =>
          WIDTHS.map((width) => (
            <ThemeFrame key={`${theme}-${width}`} theme={theme} scheme={scheme} width={width} label={`theme="${theme}" · ${width}px`} src={`/dev/ui?preview=1&theme=${theme}&scheme=${scheme}`} />
          )),
        )}
      </Stack>
    </Page>
  );
}

function Gallery({ theme }: { theme: ThemeName }) {
  const [loading, setLoading] = useState(false);
  const [seg, setSeg] = useState("event");
  const [search, setSearch] = useState("");
  const [venue, setVenue] = useState("c");
  const [picker, setPicker] = useState("check");
  const [money, setMoney] = useState<number | null>(257500);
  const [on, setOn] = useState(true);
  const [sheet, setSheet] = useState(false);
  const [day, setDay] = useState<string | null>("2026-09-19");
  const [signature, setSignature] = useState<string | null>(null);
  const [reviewed, setReviewed] = useState<Partial<Record<"contacts" | "schedule" | "pricing", boolean>>>({});
  const contact = { firstName: "Testy", lastName: "McTest", companyName: "ABC Engineering", title: "Event planner", email: "planner@example.com", phone: "+12515550100", smsConsent: 1, emailOptOut: 0, dnc: 0 };
  const card = leadCardLines({ status: "tour_scheduled", eventName: "ABC Holiday Party", eventDateFlexible: true, guestCountMin: 100, guestCountMax: 150 }, contact, { name: "The Courtyard on Dauphin" }, { name: "Corporate" });
  return (
    <AppFrame company="Example Hospitality" user="Sample Staff" role="member" pathname="/today" onNavigate={() => undefined} navigation={[{ key: "today", path: "/today", label: "Today", icon: CalendarCheck }, { key: "leads", path: "/leads", label: "Leads", icon: Inbox }]}>
      <NavBar title="Leads" onBack={() => undefined} trailing={<NavBarAction label="Add lead" icon={<Plus aria-hidden size={26} />} />} />
      <Page>
        <Stack gap={5}>
          <Stack gap={1}>
            <Text variant="large-title">Large title 28</Text>
            <Text variant="title">Title 22</Text>
            <Text variant="headline">Headline 17 semibold</Text>
            <Text variant="body">Body 17 — the quick brown fox jumps over the lazy dog.</Text>
            <Text variant="callout" tone="secondary">
              Callout 15 · secondary tone
            </Text>
            <Text variant="caption" tone="tertiary">
              Caption 13 · tertiary tone
            </Text>
            <Text variant="body" tabular>
              Tabular money: $2,575.00 · $12,400.00 · $100.00
            </Text>
          </Stack>

          <Stack gap={2}>
            <Text variant="headline">Contracts · review · files</Text>
            <Card><Stack gap={4}>
              <SignaturePad value={signature} onChange={setSignature} />
              <ReviewChecklist items={[{ key: "contacts", label: "Client & contacts", description: "Confirm who will sign the agreement." }, { key: "schedule", label: "Dates & times", description: "Access starts through everyone out." }, { key: "pricing", label: "Pricing & total" }]} value={reviewed} onChange={(key, checked) => setReviewed(previous => ({ ...previous, [key]: checked }))} onEdit={() => setSheet(true)} />
            </Stack></Card>
            <RowGroup>
              <FileRow filename="Signed venue agreement — version 2.pdf" contentType="application/pdf" size={152400} kind="contract" linked detail="Sep 19, 2026" onOpen={() => setSheet(true)} />
              <FileRow filename="Example event floor plan.png" contentType="image/png" size={640000} kind="floor_plan" detail="Sep 19, 2026" attribution="Uploaded by Sample Staff" onOpen={() => setSheet(true)} />
            </RowGroup>
          </Stack>

          <Stack gap={2}>
            <Text variant="headline">Buttons</Text>
            <Button>Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="destructive">Destructive</Button>
            <Button loading={loading} onClick={() => setLoading(true)}>
              {loading ? "Loading…" : "Tap to load"}
            </Button>
            <Inline gap={2} wrap>
              <Button fullWidth={false} icon={<Mail aria-hidden size={18} />}>
                With icon
              </Button>
              <Button fullWidth={false} variant="secondary" disabled>
                Disabled
              </Button>
            </Inline>
          </Stack>

          <Stack gap={2}>
            <Text variant="headline">Status chips</Text>
            <Inline gap={2} wrap>
              <StatusChip>Neutral</StatusChip>
              <StatusChip tone="success">Confirmed</StatusChip>
              <StatusChip tone="warn">Hold expires 2d</StatusChip>
              <StatusChip tone="danger">Overdue</StatusChip>
              <StatusChip tone="accent">New</StatusChip>
            </Inline>
          </Stack>

          <Stack gap={2}>
            <Text variant="headline">Rows</Text>
            <RowGroup>
              <Row icon={<Phone aria-hidden size={22} />} title="Ann Walker" subtitle="Wedding · Sat Oct 10 · 120 guests" trailing={<StatusChip tone="accent">New</StatusChip>} onClick={() => undefined} />
              <Row icon={<CalendarCheck aria-hidden size={22} />} title="Courtyard on Dauphin" subtitle="Balance due $2,500" trailing={<Text variant="callout" tone="secondary" tabular as="span">$2,500</Text>} onClick={() => undefined} />
              <Row icon={<Settings aria-hidden size={22} />} title="Static row (no action)" subtitle="No chevron" />
            </RowGroup>
          </Stack>

          <Stack gap={2}>
            <Text variant="headline">Card + form field</Text>
            <Card>
              <Stack gap={3}>
                <Text variant="headline">Send hold link</Text>
                <TextField label="Email" type="email" placeholder="ann@example.com" hint="We'll text and email the link." />
                <TextField label="Phone" type="tel" defaultValue="not a phone" error="Enter a 10-digit number" />
                <Button>Send</Button>
              </Stack>
            </Card>
          </Stack>

          <FormSection title="Record and responsive templates">
            <ResponsiveFilters title="Example filters" summary="All venues · Upcoming" search={<SearchBar value={search} onChange={setSearch} placeholder="Search records" />} onReset={() => setSearch("")}>
              <FormGrid><Picker label="Sample venue" value={venue} onChange={setVenue} options={[{ value: "c", label: "Courtyard" }, { value: "h", label: "Hallett-Irby" }]} /></FormGrid>
            </ResponsiveFilters>
            <Workspace variant="overview">
              <Region area="main"><Card><LeadCard card={card} status="tour_scheduled" identity /><ContactSummary contact={contact} role="Primary" onOpen={() => setSheet(true)} onText={() => setSheet(true)} /></Card></Region>
              <Region area="aside"><RowGroup>
                <TourRow tour={{ card, leadStatus: "converted", status: "scheduled", scheduledAt: "2027-05-15T14:00", durationMin: 30, assigneeName: "Sample Staff" }} />
                <EventRow event={{ eventDate: "2027-05-15", startsAt: "2027-05-15T18:00", eventName: "ABC Holiday Party", clientName: "Testy McTest", clientCompanyName: "ABC Engineering", venueName: "Courtyard", status: "booked", balanceDue: 25000 }} />
              </RowGroup></Region>
            </Workspace>
            <SaveBar><Button variant="secondary" onClick={() => setSheet(true)}>Example editor</Button></SaveBar>
          </FormSection>

          <Stack gap={2}>
            <Text variant="headline">Form fields</Text>
            <Card>
              <Stack gap={3}>
                <Picker label="Method" value={picker} onChange={setPicker} options={[{ value: "check", label: "Check" }, { value: "cash", label: "Cash" }, { value: "zelle", label: "Zelle" }]} />
                <MoneyField label="Amount" value={money} onChange={setMoney} hint="Stored as integer cents" />
                <Inline gap={2}>
                  <TextField label="Date" type="date" className="flex-1" defaultValue="2027-05-15" />
                  <TextField label="Time" type="time" className="flex-1" defaultValue="16:00" />
                </Inline>
                <TextArea label="Notes" placeholder="Internal notes" />
                <FormField label="Any control" hint="FormField wraps whatever you give it">
                  <input type="number" defaultValue={120} className="w-full" />
                </FormField>
                <Toggle label="Damage deposit" description="Collected or held on a card" checked={on} onChange={setOn} />
              </Stack>
            </Card>
          </Stack>

          <Stack gap={2}>
            <Text variant="headline">Segmented · search · chips</Text>
            <SegmentedControl items={[{ key: "event", label: "Event" }, { key: "pricing", label: "Pricing" }, { key: "payments", label: "Payments" }, { key: "activity", label: "Activity" }]} value={seg} onChange={setSeg} />
            <SearchBar value={search} onChange={setSearch} placeholder="Search client, event, phone" />
            <ChipStrip>
              <SelectChip selected={venue === "all"} onClick={() => setVenue("all")}>All venues</SelectChip>
              <SelectChip color="#7B5EA7" selected={venue === "h"} onClick={() => setVenue("h")}>Hallett-Irby</SelectChip>
              <SelectChip color="#A6791B" selected={venue === "c"} onClick={() => setVenue("c")}>Courtyard</SelectChip>
              <SelectChip color="#2E7D5B" selected={venue === "o"} onClick={() => setVenue("o")}>Oak & Fountain</SelectChip>
            </ChipStrip>
          </Stack>

          <Stack gap={2}>
            <Text variant="headline">Month grid</Text>
            <MonthGrid
              month="2026-09"
              today="2026-09-17"
              selected={day}
              onSelect={setDay}
              bars={[
                { key: "1", from: "2026-09-19", to: "2026-09-19", color: "#A6791B", style: "solid", label: "Walker wedding" },
                { key: "2", from: "2026-09-19", to: "2026-09-20", color: "#7B5EA7", style: "striped", label: "HOLD — Reyes" },
                { key: "3", from: "2026-09-26", to: "2026-09-26", color: "#2E7D5B", style: "outline", label: "Pending — Chen" },
                { key: "4", from: "2026-09-07", to: "2026-09-08", style: "grey", label: "Closed" },
              ]}
            />
          </Stack>

          <Stack gap={2}>
            <Text variant="headline">Stats · money · avatars</Text>
            <StatRow>
              <Stat label="Collected" cents={1245000} />
              <Stat label="Outstanding" cents={310050} tone="danger" />
              <Stat label="Deposits held" cents={800000} />
            </StatRow>
            <Inline gap={3}>
              <Avatar name="Ann Walker" />
              <Avatar name="Courtyard" size="sm" ringColor="#A6791B" />
              <MoneyText cents={257500} variant="headline" />
              <MoneyText cents={-5000} tone="danger" />
            </Inline>
          </Stack>

          <Stack gap={2}>
            <Text variant="headline">Timeline</Text>
            <Card>
              <Stack gap={0}>
                <TimelineItem icon={<Banknote aria-hidden size={16} />} title="Record payment" body="check · $2,500.00 · #1042" meta="Veronica · Sep 17, 2026, 2:05 PM" />
                <TimelineItem icon={<Pencil aria-hidden size={16} />} title="Update" body="guestCount: 100 → 120 · total: $5,000.00 → $5,250.00" meta="Jared Irby · Sep 16, 2026, 9:12 AM" />
                <TimelineItem icon={<FilePlus2 aria-hidden size={16} />} title="Create" meta="Jared Irby · Sep 15, 2026, 4:40 PM" last />
              </Stack>
            </Card>
          </Stack>

          <Stack gap={2}>
            <Text variant="headline">Conversation · action bar</Text>
            <Card>
              <Stack gap={2}>
                <Bubble direction="in" caption="Sep 17, 1:58 PM · Received">
                  Hi! Is Oct 10 still open for a wedding of about 120?
                </Bubble>
                <Bubble direction="out" caption="2:05 PM · Delivered">
                  Hi Ann, it's Veronica with Yellowhammer — yes, Oct 10 is open. Want to tour this week?
                </Bubble>
                <SystemRow>Call · No answer · 3:10 PM</SystemRow>
                <Bubble direction="out" caption="3:12 PM · Failed — 21610 unsubscribed recipient">
                  Just tried you — call me back anytime.
                </Bubble>
              </Stack>
            </Card>
            <Card>
              <ActionBar
                items={[
                  { key: "text", label: "Text", icon: <MessageSquare aria-hidden size={22} />, onClick: () => undefined },
                  { key: "call", label: "Call", icon: <Phone aria-hidden size={22} />, onClick: () => undefined },
                  { key: "email", label: "Email", icon: <Mail aria-hidden size={22} />, onClick: () => undefined },
                  { key: "tour", label: "Tour", icon: <CalendarClock aria-hidden size={22} />, onClick: () => undefined },
                  { key: "book", label: "Book", icon: <CalendarCheck aria-hidden size={22} />, onClick: () => undefined, disabled: true },
                  { key: "more", label: "More", icon: <Ellipsis aria-hidden size={22} />, onClick: () => undefined },
                ]}
              />
            </Card>
          </Stack>

          <Stack gap={2}>
            <Text variant="headline">Toast · bottom sheet</Text>
            <ToastPreview message="Booking created" tone="success" />
            <ToastPreview message="Could not save — try again" tone="danger" />
            <ToastPreview message="Saved" />
            <Button variant="secondary" onClick={() => setSheet(true)}>
              Open bottom sheet
            </Button>
            <BottomSheet
              open={sheet}
              onClose={() => setSheet(false)}
              title="Record payment"
              footer={
                <Button onClick={() => setSheet(false)}>
                  Record payment
                </Button>
              }
            >
              <Stack gap={3}>
                <Picker label="Method" value={picker} onChange={setPicker} options={[{ value: "check", label: "Check" }, { value: "cash", label: "Cash" }]} />
                <MoneyField label="Amount" value={money} onChange={setMoney} />
                <TextField label="Reference" placeholder="Check no." />
                <TextField label="Received on" type="date" defaultValue="2026-09-17" />
              </Stack>
            </BottomSheet>
          </Stack>

          <Stack gap={2}>
            <Text variant="headline">Skeleton</Text>
            <Skeleton variant="rows" count={3} />
            <Skeleton variant="cards" count={1} />
          </Stack>

          <Stack gap={2}>
            <Text variant="headline">Empty state</Text>
            <Card>
              <EmptyState icon={Inbox} title="No leads yet" body="New leads from your websites will appear here." action={<Button fullWidth={false}>Add a lead</Button>} />
            </Card>
          </Stack>

          <Text variant="caption" tone="tertiary">
            theme: {theme}
          </Text>
        </Stack>
      </Page>
      <TabBar items={TABS} active="leads" onSelect={() => undefined} />
    </AppFrame>
  );
}
